import client from './api'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { generatePath } from 'react-router-dom'
import { SearchAppInfoResponse } from 'domain/SearchAppInfo'
import { AppInfo, Versions } from 'domain/AppInfo'
import { AppStatus } from 'domain/AppStatus'
import { AppCategory } from 'domain/AppCategory'
import { LoginResponse } from 'domain/LoginResponse'
import { API_ROUTES } from 'app/components/AppRouter/routes'
import { SearchAppGridResponse } from 'domain/SearchAppGrid'
import { PaginationParams } from 'domain/PaginationMeta'
import { User, UsersResponse } from 'domain/User'
import { CdnInfo } from 'domain/CdnInfo'

export const getApps = (
    params?: Partial<PaginationParams>,
): Promise<AxiosResponse<SearchAppInfoResponse>> => {
    return client.get<SearchAppInfoResponse>('/application/search', {
        params: { ...params },
    })
}

export const getAppsGrid = (): Promise<
    AxiosResponse<SearchAppGridResponse>
> => {
    return client.get<SearchAppGridResponse>('/application/dashboard')
}

export const getApp = (id: string): Promise<AxiosResponse<AppInfo>> => {
    return client.get<AppInfo>(`/application/${id}`)
}

export const getAppByPackageName = (
    packageName: string,
): Promise<AxiosResponse<AppInfo>> => {
    return client.get<AppInfo>(`/application/package/${packageName}`)
}

export const updateAppService = (
    id: string,
    updatedApp: AppInfo,
    config: AxiosRequestConfig<AppInfo>,
): Promise<AxiosResponse<AppInfo>> => {
    return client.put(`/application`, updatedApp, config)
}

export const getAppApks = (id: string): Promise<AxiosResponse<Versions[]>> => {
    return client.get(`/application/${id}/apk`)
}

export const getAppApk = (
    id: string,
    apkId: string,
): Promise<AxiosResponse<Versions>> => {
    return client.get(`/application/${id}/apk/${apkId}`)
}

export const checkApkVersionCode = (
    id: string,
    versionCode: string,
    config: AxiosRequestConfig,
) => {
    return client.get(`/application/${id}/check`, {
        params: { isVersionCodeExists: versionCode },
        ...config,
    })
}

export const createAppApk = (
    id: string,
    apkInfo: Versions,
    config: AxiosRequestConfig<Versions>,
) => {
    return client.post(`/application/${id}/apk`, apkInfo, config)
}

export const updateAppApk = (
    id: string,
    apkInfo: Versions,
    config: AxiosRequestConfig<Versions>,
) => {
    return client.put(`/application/${id}/apk/${apkInfo.id}`, apkInfo, config)
}

export const getAppStatuses = (): Promise<AxiosResponse<AppStatus[]>> => {
    return client.get<AppStatus[]>('/status')
}

export const getAppCategories = (): Promise<AxiosResponse<AppCategory[]>> => {
    return client.get<AppCategory[]>('/category')
}

export const createNewApp = (newApp: AppInfo, config: AxiosRequestConfig) => {
    return client.post('/application', newApp, config)
}

export const downloadApk = (appId: string, apkId: string) => {
    return client.get(
        generatePath(API_ROUTES.DOWNLOAD_APK, { appid: appId, apkid: apkId }),
    )
}

export const uploadFormAPK = (
    appId: string,
    apkId: string,
    formData: FormData,
    config: AxiosRequestConfig,
) => {
    return client.post(
        generatePath(API_ROUTES.UPLOAD_APK, {
            appid: appId,
            apkid: apkId,
        }),
        formData,
        config,
    )
}

export const loginService = (
    login: string,
    pass: string,
): Promise<AxiosResponse<LoginResponse>> => {
    return client.post('/login', { username: login, password: pass })
}

export const getUsers = (
    config: AxiosRequestConfig,
): Promise<AxiosResponse<UsersResponse>> => {
    return client.get<UsersResponse>('/users', config)
}

export const registerUser = (
    login: string,
    level: number,
    config: AxiosRequestConfig,
): Promise<AxiosResponse<User>> => {
    return client.post<User>(
        '/register',
        { login: login, level: level },
        config,
    )
}

export const resetUserPassword = (
    login: string,
    config: AxiosRequestConfig,
): Promise<AxiosResponse<User>> => {
    return client.put<User>('/reset', { login: login }, config)
}

export const getAppIconUrl = (id: string | number, cdnUrl?: string) => {
    if (id === undefined) return ''

    if (cdnUrl) {
        return cdnUrl + `/api/application/${id}/getCover`
    }

    return `/api/application/${id}/getCover`
}

export const getAppDownloadUrl = (
    id: string | number,
    apkId: string | number,
    cdnUrl?: string,
) => {
    if (id === undefined || apkId === undefined) return ''

    if (cdnUrl) {
        return cdnUrl + `/api/application/${id}/apk/${apkId}/download`
    }

    return `/api/application/${id}/apk/${apkId}/download`
}

export const getCdnSettings = (): Promise<AxiosResponse<CdnInfo>> => {
    return client.get<CdnInfo>('/settings/cdn')
}

export const getCdnConfiguration = (): Promise<AxiosResponse<CdnInfo>> => {
    return client.get<CdnInfo>('/configuration/cdn')
}
